import { PayloadAction } from "@reduxjs/toolkit";
import { createApi, createSlice } from "utils/@reduxjs/toolkit";
import { useInjectReducer } from "utils/redux-injectors";
import { baseQuery, endpoints, formatErrors } from 'utils/api/endpoints';
import { SigninState } from "./types";

export const initialState: SigninState = {
  isLoading: false,
  error: null,
  data: null,
};

// RTK Query API for registration
export const api = createApi({
  reducerPath: 'signinApi',
  tagTypes: ['Auth'],
  baseQuery,
  endpoints: builder => ({
    // Initiate registration
    initiateRegistration: builder.mutation<any, { email: string }>({
      query: (data) => ({
        url: endpoints.registerInitiate.url,
        method: endpoints.registerInitiate.method,
        body: data,
      }),
      transformResponse: (response: any) => response || [],
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    // Verify registration code
    verifyRegistrationCode: builder.mutation<any, { email: string; code: string }>({
      query: (data) => ({
        url: endpoints.registerVerifyCode.url,
        method: endpoints.registerVerifyCode.method,
        body: data,

      }),
      transformResponse: (response: any) => response || [],
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    // Resend verification code
    resendVerificationCode: builder.mutation<any, { email: string }>({
      query: (data) => ({
        url: endpoints.resendVerificationCodeRegister.url,
        method: endpoints.resendVerificationCodeRegister.method,
        body: data,
      }),
      transformResponse: (response: any) => response || [],
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    // Set password
    setPassword: builder.mutation<any, { email: string; password: string }>({
      query: (data) => ({
        url: endpoints.registerSetPassword.url,
        method: endpoints.registerSetPassword.method,
        body: data,
      }),
      transformResponse: (response: any) => response || [],
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    // Login
    login: builder.mutation<any, { email: string; password: string }>({
      query: (data) => ({
        url: endpoints.login.url,
        method: endpoints.login.method,
        body: data,
      }),
    }),
    // Logout
    logout: builder.mutation<any, void>({
      query: () => ({
        url: endpoints.logout.url,
        method: endpoints.logout.method,
      }),
    }),
  }),
});

const slice = createSlice({
  name: "signin",
  initialState,
  reducers: {
    someAction(state, action: PayloadAction<any>) {},
  },
});

export const { actions: signinActions, reducer: signinReducer } = slice;

export const useSigninSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });

  // RTK Query injects itself
  return {
    actions: slice.actions,
    ...api,
  };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
